import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetAdminWhatsAppOverviewAPI, GetAdminWhatsAppCompaniesAPI } from '../../../../api/AdminAPI';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 25, pages: 1 };
const DEFAULT_OVERVIEW = {
  generated_at: null,
  period_days: 7,
  platform_enabled: false,
  policy_mode: null,
  platform_instance: {
    available: false,
    last_status: null,
    last_status_checked_at: null,
    last_status_error: null,
    alerted_at: null,
  },
  platform_sends: { today: 0, last_7_days: 0, last_30_days: 0 },
  companies_by_origin: { platform: 0, own_connected: 0, own_disconnected: 0 },
  blocked_in_period: { rate_limited: 0, policy_blocked: 0 },
  limit_alerts_in_period: {
    total: 0,
    by_limit: { per_minute: 0, per_day: 0, global_per_minute: 0, global_per_day: 0 },
  },
  categories: [],
  sends_by_category: {},
};
const DEFAULT_DAYS = 7;
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useAdminWhatsApp
 *
 * Hook responsável pela tela "WhatsApp" do Admin Geral: status da instância compartilhada
 * e consumo por empresa (overview.php + companies.php). Filtros (período, busca, origem,
 * incluir teste, ordenação, página) vivem na URL via useSearchParams, no mesmo padrão de
 * useAdminAudit/useAdminLogs, para que o link possa ser compartilhado.
 *
 * @returns {Object} estado e ações da tela
 */
function useAdminWhatsApp() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtros (na URL) ───────────────────────────────────────────────────────
  const daysParam = parseInt(searchParams.get('days'), 10);
  const days = [7, 15, 30].includes(daysParam) ? daysParam : DEFAULT_DAYS;
  const q = searchParams.get('search') || '';
  const origin = searchParams.get('origin') || 'all';
  const includeTest = searchParams.get('include_test') === '1';
  const sort = searchParams.get('sort') || 'sends';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  const setDays = useCallback((value) => updateParams({ days: value === DEFAULT_DAYS ? undefined : value }), [updateParams]);
  const setOrigin = useCallback((value) => updateParams({ origin: value === 'all' ? undefined : value }), [updateParams]);
  const setIncludeTest = useCallback((value) => updateParams({ include_test: value ? '1' : undefined }), [updateParams]);
  const setSort = useCallback((value) => updateParams({ sort: value === 'sends' ? undefined : value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);

  // ─── Busca (draft local + debounce -> URL) ─────────────────────────────────
  const [searchInput, setSearchInputState] = useState(q);
  const searchTimer = useRef(null);

  useEffect(() => { setSearchInputState(q); }, [q]);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => updateParams({ search: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  // ─── Visão geral (overview.php) ─────────────────────────────────────────────
  const [overview, setOverview] = useState(DEFAULT_OVERVIEW);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const fetchOverview = useCallback(() => {
    setOverviewLoading(true);
    setOverviewError(null);
    GetAdminWhatsAppOverviewAPI(
      { days, include_test: includeTest ? 1 : 0 },
      (response) => {
        if (!response || response.success === false) {
          setOverviewError(response?.error || 'Erro ao carregar a visão geral do WhatsApp. Tente novamente.');
          setOverviewLoading(false);
          return;
        }
        setOverview(response);
        setOverviewLoading(false);
      }
    );
  }, [days, includeTest]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // ─── Empresas (companies.php) ───────────────────────────────────────────────
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState(null);
  const [companiesCategories, setCompaniesCategories] = useState([]);

  const fetchCompanies = useCallback(() => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    GetAdminWhatsAppCompaniesAPI(
      {
        days,
        search: q,
        origin,
        include_test: includeTest ? 1 : 0,
        page,
        limit: DEFAULT_PAGINATION.limit,
        sort,
      },
      (response) => {
        if (!response || response.success === false) {
          setCompaniesError(response?.error || 'Erro ao carregar as empresas. Tente novamente.');
          setCompaniesLoading(false);
          return;
        }
        setCompanies(response.companies || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page });
        setCompaniesCategories(response.categories || []);
        setCompaniesLoading(false);
      }
    );
  }, [days, q, origin, includeTest, page, sort]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const categories = (overview.categories && overview.categories.length > 0)
    ? overview.categories
    : companiesCategories;
  const sendsByCategory = overview.sends_by_category || {};

  return {
    overview,
    overviewLoading,
    overviewError,
    categories,
    sendsByCategory,

    companies,
    pagination,
    companiesLoading,
    companiesError,

    days,
    setDays,
    searchInput,
    setSearchInput,
    origin,
    setOrigin,
    includeTest,
    setIncludeTest,
    sort,
    setSort,
    page,
    setPage,

    refresh: () => { fetchOverview(); fetchCompanies(); },
  };
}

export default useAdminWhatsApp;
